/* eslint-disable @typescript-eslint/no-use-before-define */
import { SuspenseContentWrapper } from '@euler/app/components/SuspenseContentWrapper';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { InspectionTemplatePreviewNavParams } from '@euler/app/flows/task/pages/detail/InspectionTemplatePreviewFlow';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAdaptiveContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { InspectionTemplate } from '@euler/model';
import { comparer, isNotNull } from '@euler/utils';
import { sorted, uniq } from '@euler/utils/array';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { SectionList, SectionListData } from 'react-native';
import { runOnUI, useSharedValue } from 'react-native-reanimated';
import { HiddenSiteBottomSheet } from '../inspection/components/HiddenSiteBottomSheet';
import { InspectionNavHeader } from '../inspection/components/InspectionNavHeader';
import { InspectionSiteGridView } from '../inspection/components/InspectionSiteGrid';
import { InspectionSiteGridHeader } from '../inspection/components/InspectionSiteGridHeader';
import * as InspectionSiteGrid from '../inspection/functions/InspectionSiteGrid';

type Props = {
  template: InspectionTemplate;
};

export const InspectionTemplatePreviewScreen = wrapNavigatorScreen(
  (props: Props) => {
    return (
      <SuspenseContentWrapper
        ErrorBoundaryComponent={null}
        contentContainerStyle={{
          justifyContent: 'center',
        }}
      >
        <Content {...props} />
      </SuspenseContentWrapper>
    );
  },
  { headerShown: false },
);

const Content = memo(({ template }: Props) => {
  const { rpx } = useResponsiveContext();
  const navigation =
    useNavigation<StackNavigationProp<InspectionTemplatePreviewNavParams>>();
  const activeIndex = useSharedValue(0);
  const listRef = useRef<SectionList>(null);
  const width = useAdaptiveContainerWidth();
  const { inventoryQuery } = useTaskContext();
  const { safeAreaInsets } = useSystemMetrics();
  const [navHeaderHeight, setNavHeaderHeight] = useState(0);

  const [activeSectionForHiddenSites, setActiveSectionForHiddenSites] =
    useState<InspectionSiteGrid.GroupSection>();

  const sections = useMemo(
    () =>
      InspectionSiteGrid.buildGroupSectionsFromTemplate(
        undefined,
        inventoryQuery,
        template,
      ),
    [template, inventoryQuery],
  );

  const scrollerItems = useMemo(() => sections.map(x => x.title), [sections]);

  const onScrollerItemPress = useCallback((index: number) => {
    listRef.current?.scrollToLocation({ sectionIndex: index, itemIndex: 0 });
  }, []);

  const onSearchInputPress = useCallback(() => {
    const sites = uniq(
      sections.flatMap(x => [...x.data[0].items, ...(x.hiddenSites ?? [])]),
      x => x.siteId,
    );
    navigation.push('_inspectionSiteSearch', {
      sites,
    });
  }, [navigation, sections]);

  const scrollY = useSharedValue(0);

  const onScroll = useCallback(
    (value: number) => {
      'worklet';
      scrollY.value = value;
    },
    [scrollY],
  );

  return (
    <>
      <InspectionNavHeader
        activeIndex={activeIndex}
        scrollerItems={scrollerItems}
        hideRightButtons
        onScrollerItemPress={onScrollerItemPress}
        onSearchInputPress={onSearchInputPress}
        onLayout={e => setNavHeaderHeight(e.nativeEvent.layout.height)}
      />
      <SectionList
        ref={listRef as any}
        contentContainerStyle={{
          paddingBottom: safeAreaInsets.bottom + rpx(15),
        }}
        onScroll={e => {
          runOnUI(onScroll)(e.nativeEvent.contentOffset.y);
        }}
        showsVerticalScrollIndicator={false}
        stickySectionHeadersEnabled={false}
        sections={sections}
        initialNumToRender={10}
        keyExtractor={x => x.groupId}
        viewabilityConfig={{
          itemVisiblePercentThreshold: 95,
          minimumViewTime: 50,
        }}
        renderSectionHeader={({
          section,
        }: {
          section: SectionListData<
            InspectionSiteGrid.GroupSectionDataItem,
            InspectionSiteGrid.GroupSection
          >;
        }) => {
          return (
            <InspectionSiteGridHeader
              hasMore={
                section.hiddenSites != null && section.hiddenSites.length > 0
              }
              width={width}
              name={section.title}
              groupId={section.groupId}
              onMorePress={() => setActiveSectionForHiddenSites(section)}
            />
          );
        }}
        renderItem={({ item: section }) => (
          <InspectionSiteGridView items={section.items} width={width} />
        )}
        onViewableItemsChanged={({ viewableItems }) => {
          const visibleSectionIndics = sorted(
            uniq(
              viewableItems
                .filter(x => isNotNull(x.section))
                .map(x => {
                  const section = x.section! as InspectionSiteGrid.GroupSection;
                  return section.index;
                }),
              x => x,
            ),
            comparer,
          );
          activeIndex.value = visibleSectionIndics[0] ?? 0;
        }}
      />
      <HiddenSiteBottomSheet
        show={activeSectionForHiddenSites != null}
        items={activeSectionForHiddenSites?.hiddenSites ?? []}
        topMargin={navHeaderHeight}
        onDismiss={() => setActiveSectionForHiddenSites(undefined)}
      />
    </>
  );
});
